// PostPoll.tsx — renders a post's attached poll: options with vote counts + percentage
// bars, tap to vote / switch vote, viewer's pick highlighted, closed state shows final
// results with no interaction. Optimistic update with rollback on a failed/409 vote.

import { useState } from 'react';
import styled from 'styled-components';
import type { Poll } from '@/core/models/post';
import { usePosts, useRequireAuth } from '@/hooks';
import { MetaRow, useToast } from '@/ui';
import { formatCount } from '@/components/formatCount';
import { ApiError } from '@/core/lib/apiClient';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
`;

const OptionBtn = styled.button<{ $picked: boolean; $closed: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ $picked, theme }) =>
    $picked ? `color-mix(in srgb, ${theme.color.accentCyan} 14%, transparent)` : 'transparent'};
  overflow: hidden;
  cursor: ${({ $closed }) => ($closed ? 'default' : 'pointer')};
  text-align: left;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const FillBar = styled.span<{ $pct: number; $picked: boolean }>`
  position: absolute;
  inset: 0;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $picked, theme }) => ($picked ? `${theme.color.accentCyan}33` : theme.color.glassBorder)};
  z-index: 0;
  transition: width 0.2s ease;
`;

const OptionText = styled.span`
  position: relative;
  z-index: 1;
`;

const OptionPct = styled.span`
  position: relative;
  z-index: 1;
  font-weight: 700;
  flex-shrink: 0;
`;

const MetaLine = styled.p`
  margin: 2px 0 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface PostPollProps {
  postId: string;
  poll: Poll;
}

/** True for a 409 the server sends when a poll can no longer be voted on. */
function isPollClosedError(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false;
  return e.status === 409 || e.code === 'poll_closed' || e.code === 'closed';
}

export function PostPoll({ postId, poll }: PostPollProps) {
  const { pollVote } = usePosts();
  const requireAuth = useRequireAuth();
  const toast = useToast();
  const [pending, setPending] = useState(false);
  // The server closed this poll after we last rendered it (a genuine race, not the
  // stale-client case — that's blocked below). Sticky once true: postsService's
  // rollback restores the pre-vote poll snapshot, which would otherwise silently
  // un-close this and keep inviting votes the server will just reject again.
  const [closedOverride, setClosedOverride] = useState(false);
  const closed = poll.closed || closedOverride;

  function handleVote(optionId: string) {
    if (closed || pending) return;
    if (!requireAuth('vote')) return;
    if (optionId === poll.viewerOptionId) return;
    setPending(true);
    void pollVote(postId, optionId)
      .catch((e: unknown) => {
        if (isPollClosedError(e)) {
          setClosedOverride(true);
          toast('This poll is closed.');
        } else {
          toast("Couldn't record your vote. Please try again.");
        }
      })
      .finally(() => setPending(false));
  }

  return (
    <Wrap data-testid="post-poll" onClick={(e) => e.stopPropagation()}>
      {poll.options.map((opt) => {
        const picked = opt.id === poll.viewerOptionId;
        return (
          <OptionBtn
            key={opt.id}
            type="button"
            $picked={picked}
            $closed={closed}
            aria-pressed={picked}
            aria-label={`${opt.text}: ${opt.pct}% (${formatCount(opt.votes)} votes)`}
            disabled={closed}
            onClick={() => handleVote(opt.id)}
          >
            <FillBar $pct={opt.pct} $picked={picked} aria-hidden="true" />
            <OptionText>{opt.text}</OptionText>
            <OptionPct>{opt.pct}%</OptionPct>
          </OptionBtn>
        );
      })}
      <MetaLine>
        <MetaRow>
          <span>{formatCount(poll.totalVotes)} votes</span>
          <span>{closed ? 'Final results' : 'Tap to vote'}</span>
        </MetaRow>
      </MetaLine>
    </Wrap>
  );
}
